import React, { useEffect, useState, useMemo } from "react";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";

const POSITION_ORDER = [
  "Kepala Sekolah",
  "Wakasek Kurikulum",
  "Wakasek Kesiswaan",
  "Wakasek Humas",
  "Wakasek Sarpras",
  "Kepala Tata Usaha",
  "Guru",
  "Guru BK",
  "Pengelola Perpustakaan",
  "Staf Tata Usaha",
  "Staf Kebersihan & Keamanan",
];

const sortTeachers = (list) =>
  [...list].sort((a, b) => {
    const ia = POSITION_ORDER.indexOf(a.position);
    const ib = POSITION_ORDER.indexOf(b.position);
    const pa = ia === -1 ? 999 : ia;
    const pb = ib === -1 ? 999 : ib;
    if (pa !== pb) return pa - pb;
    return a.name.localeCompare(b.name);
  });

export default function Teachers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Semua");
  useEffect(() => {
    api.get("/teachers?limit=200").then(({ data }) => setItems(sortTeachers(data.items))).catch(() => {});
  }, []);

  const positions = useMemo(() => ["Semua", ...POSITION_ORDER.filter((p) => items.some((i) => i.position === p))], [items]);
  const filtered = filter === "Semua" ? items : items.filter((i) => i.position === filter);

  const stats = useMemo(() => {
    const leaders = items.filter((i) => POSITION_ORDER.slice(0, 6).includes(i.position)).length;
    const teachersCount = items.filter((i) => i.position === "Guru" || i.position === "Guru BK").length;
    const staff = items.filter((i) => ["Staf Tata Usaha", "Staf Kebersihan & Keamanan", "Pengelola Perpustakaan"].includes(i.position)).length;
    return { total: items.length, leaders, teachersCount, staff };
  }, [items]);

  return (
    <div data-testid="teachers-page">
      <PageHero
        eyebrow="Pendidik"
        title="Guru & Tenaga Kependidikan"
        subtitle={`Mengenal ${stats.total} pendidik & tenaga kependidikan SMAS YPK 2 BIAK — kepala sekolah, wakasek, guru, dan staf yang berdedikasi mencetak generasi GARAM.`}
        image="https://images.unsplash.com/photo-1577896851231-70ef18881754?w=2000&q=80"
      />

      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            { label: "Total PTK", value: stats.total },
            { label: "Pimpinan Sekolah", value: stats.leaders },
            { label: "Guru Mata Pelajaran", value: stats.teachersCount },
            { label: "Staf & Pendukung", value: stats.staff },
          ].map((s) => (
            <div key={s.label} className="card-elevated p-5">
              <div className="font-serif text-3xl font-black text-[#0B1D3A]">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {positions.map((p) => (
              <button
                key={p}
                data-testid={`teacher-filter-${p.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(p)}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  filter === p ? "bg-[#0B1D3A] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((t) => {
              const isLeader = POSITION_ORDER.slice(0, 6).includes(t.position);
              return (
                <div
                  key={t.id}
                  data-testid={`teacher-${t.id}`}
                  className={`card-elevated p-5 text-center ${isLeader ? "ring-1 ring-amber-400/40" : ""}`}
                >
                  <div className="aspect-square w-full rounded-xl overflow-hidden mb-4 bg-slate-100 relative">
                    <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />
                    {isLeader && (
                      <div className="absolute top-2 left-2 bg-amber-500 text-[#0B1D3A] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                        Pimpinan
                      </div>
                    )}
                  </div>
                  <div className="font-serif font-bold text-[#0B1D3A] leading-tight">
                    {t.name}
                    {t.title ? `, ${t.title}` : ""}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{t.position}</div>
                  {t.subject && (
                    <div className="mt-2 text-xs text-amber-700 font-semibold inline-block px-2 py-0.5 bg-amber-50 rounded-full">
                      {t.subject}
                    </div>
                  )}
                  {t.bio && <p className="mt-3 text-xs text-slate-500 line-clamp-3">{t.bio}</p>}
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-500">Tidak ada data untuk filter ini.</div>
          )}
        </div>
      </section>
    </div>
  );
}
